import React, { createContext, useContext, useState, useEffect } from 'react';
import onTerminalRoute from '../utils/onTerminalRoute';
import useAuth from '../hooks/useAuth';
import API_BASE from '../config';

/**
 * TPSLContext - Global state management for TP% and SL ratio selection
 * Handles persistence, API calls, and synchronization across components
 * NEW: Supports separate TP levels and SL ratios for more realistic trading
 */
const TPSLContext = createContext();

// Default configuration
const DEFAULT_TP_LEVEL = 0.5;
const DEFAULT_TP_SL_RATIO = 4.0;  // TP:SL = 4:1 (risk 0.125% to gain 0.5%)
const STORAGE_KEY_TP = 'user_tp_level';
const STORAGE_KEY_TP_SL_RATIO = 'user_tp_sl_ratio';

// Available TP levels. Mirrors API_TP_LEVELS in the backend's
// utils/tp_sl_constants.py — 10%/15% added 2026-08-16.
export const TP_LEVELS = [0.5, 1.0, 2.0, 3.0, 5.0, 10.0, 15.0];

// Available TP:SL ratios (4 ratios) - 1:1, 1:2, 1:3, 1:4 (5:1 removed for practical risk management)
// Higher is better (risk less to gain more)
export const TP_SL_RATIOS = [1.0, 2.0, 3.0, 4.0];

// Backward compatibility
export const TP_SL_LEVELS = TP_LEVELS;

// Risk profile mappings
export const getRiskProfile = (level) => {
  if (level <= 1.0) return { 
    label: 'Conservative', 
    color: 'text-blue-600 dark:text-blue-400',
    description: 'Lower risk, higher win rate'
  };
  if (level <= 3.0) return {
    label: 'Balanced',
    color: 'text-green-600 dark:text-green-400',
    description: 'Moderate risk, balanced returns'
  };
  if (level <= 5.0) return {
    label: 'Growth',
    color: 'text-yellow-600 dark:text-yellow-400',
    description: 'Higher returns, moderate risk'
  };
  // 10%/15%: wide targets take far longer to resolve (avg 13 and 25 days vs
  // ~4 at 5%) and carry the weakest measured expectancy of the grid.
  return {
    label: 'Wide target',
    color: 'text-red-600 dark:text-red-400',
    description: 'Rarely reached; long holds'
  };
};

// Combined risk profile based on TP level and TP:SL ratio
export const getCombinedRiskProfile = (tpLevel, tpSlRatio) => {
  // Higher TP:SL ratio is BETTER (less risk), so we invert the ratio component
  // Normalised against the widest TP so the bands keep their meaning as levels
  // are added (was /5.0, which pinned every level above 5% to "High Risk"
  // regardless of ratio once 10%/15% shipped).
  const maxTp = TP_LEVELS[TP_LEVELS.length - 1];
  const riskScore = (tpLevel / maxTp) + (1.0 / tpSlRatio); // Higher ratio = lower risk
  
  if (riskScore <= 0.8) return { 
    label: 'Conservative', 
    color: 'text-blue-600 dark:text-blue-400',
    description: 'Low risk, excellent risk/reward'
  };
  if (riskScore <= 1.2) return { 
    label: 'Moderate', 
    color: 'text-green-600 dark:text-green-400',
    description: 'Balanced risk/reward'
  };
  if (riskScore <= 1.6) return { 
    label: 'Aggressive', 
    color: 'text-yellow-600 dark:text-yellow-400',
    description: 'Higher risk, moderate risk/reward'
  };
  return { 
    label: 'High Risk', 
    color: 'text-red-600 dark:text-red-400',
    description: 'High risk, poor risk/reward'
  };
};

export const TPSLProvider = ({ children }) => {
  const { userId, token } = useAuth();
  const [selectedLevel, setSelectedLevel] = useState(DEFAULT_TP_LEVEL); // Backward compatibility
  const [selectedTpLevel, setSelectedTpLevel] = useState(DEFAULT_TP_LEVEL);
  const [selectedTpSlRatio, setSelectedTpSlRatio] = useState(DEFAULT_TP_SL_RATIO);
  // KAN-64 item 5 + owner follow-up (26 Jul): whether risk terms were chosen
  // by the user THIS SESSION. Deliberately NOT hydrated from the stored/API
  // preference — like the universe, TP/SL is never auto-populated; every
  // session opens blank ("Set risk terms") and Scan stays gated until the
  // user picks. Stored values still hydrate selectedTpLevel/Ratio silently
  // (legacy surfaces, the ticket's explicit yardstick tap) but are never
  // DISPLAYED as a choice until re-picked.
  const [riskTermsChosen, setRiskTermsChosen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [levelStats, setLevelStats] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null); // Track pending changes to prevent overlapping calls

  // Load user's saved TP level and SL ratio on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        // First try localStorage for immediate response
        const storedTpLevel = localStorage.getItem(STORAGE_KEY_TP);
        const storedTpSlRatio = localStorage.getItem(STORAGE_KEY_TP_SL_RATIO);
        
        if (storedTpLevel && TP_LEVELS.includes(parseFloat(storedTpLevel))) {
          const tpLevel = parseFloat(storedTpLevel);
          setSelectedTpLevel(tpLevel);
          setSelectedLevel(tpLevel); // Backward compatibility
        }

        if (storedTpSlRatio && TP_SL_RATIOS.includes(parseFloat(storedTpSlRatio))) {
          setSelectedTpSlRatio(parseFloat(storedTpSlRatio));
        }

        // If user is logged in, fetch from API.
        // The terminal keeps its exit grid in mypicks/portfolio-settings
        // (exit_tp_pct / exit_sl_ratio) via useExitGrid — a different table
        // from this legacy user_tp_sl_preferences one. Nothing under /terminal
        // reads this context, so the request is skipped there.
        if (userId && token && !onTerminalRoute()) {
          const response = await fetch(`${API_BASE}/user/tp-sl-preferences`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              // Load TP level
              if (data.data.selected_tp_percent) {
                const apiTpLevel = parseFloat(data.data.selected_tp_percent);
                if (TP_LEVELS.includes(apiTpLevel)) {
                  setSelectedTpLevel(apiTpLevel);
                  setSelectedLevel(apiTpLevel); // Backward compatibility
                  localStorage.setItem(STORAGE_KEY_TP, apiTpLevel.toString());
                }
              }
              
              // Load TP:SL ratio
              if (data.data.selected_sl_ratio) {
                const apiTpSlRatio = parseFloat(data.data.selected_sl_ratio);
                if (TP_SL_RATIOS.includes(apiTpSlRatio)) {
                  setSelectedTpSlRatio(apiTpSlRatio);
                  localStorage.setItem(STORAGE_KEY_TP_SL_RATIO, apiTpSlRatio.toString());
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading user TP/SL preferences:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadUserPreferences();
  }, [userId, token]);

  // Save TP level changes to API and localStorage
  const saveUserTpLevel = async (tpLevel) => {
    try {
      localStorage.setItem(STORAGE_KEY_TP, tpLevel.toString());

      if (userId && token) {
        const response = await fetch(`${API_BASE}/user/tp-sl-preferences`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            selected_tp_percent: tpLevel,
            selected_sl_ratio: selectedTpSlRatio,
            auto_adjust: false,
            risk_profile: getCombinedRiskProfile(tpLevel, selectedTpSlRatio).label.toLowerCase()
          })
        });

        if (!response.ok) {
          console.error('Failed to save TP level to server:', await response.text());
        }
      }
    } catch (error) {
      console.error('Error saving TP level:', error);
    }
  };

  // Save TP:SL ratio changes to API and localStorage
  const saveUserTpSlRatio = async (slRatio) => {
    try {
      localStorage.setItem(STORAGE_KEY_TP_SL_RATIO, slRatio.toString());

      if (userId && token) {
        const response = await fetch(`${API_BASE}/user/tp-sl-preferences`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            selected_tp_percent: selectedTpLevel,
            selected_sl_ratio: slRatio,
            auto_adjust: false,
            risk_profile: getCombinedRiskProfile(selectedTpLevel, slRatio).label.toLowerCase()
          })
        });

        if (!response.ok) {
          console.error('Failed to save SL ratio to server:', await response.text());
        }
      }
    } catch (error) {
      console.error('Error saving SL ratio:', error);
    }
  };

  // Change selected level with persistence (backward compatibility)
  const changeLevel = async (newLevel) => {
    if (!TP_LEVELS.includes(newLevel)) {
      console.error('Invalid TP level:', newLevel);
      return;
    }

    setIsLoading(true);
    setSelectedLevel(newLevel);
    setSelectedTpLevel(newLevel);
    
    try {
      await saveUserTpLevel(newLevel);
      
      // Preload stats for the new combination
      await fetchLevelStats(newLevel, selectedTpSlRatio);
      
    } catch (error) {
      console.error('Error changing TP level:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Change TP level independently
  const changeTpLevel = async (newTpLevel) => {
    if (!TP_LEVELS.includes(newTpLevel)) {
      console.error('Invalid TP level:', newTpLevel);
      return;
    }

    // Prevent overlapping changes
    if (isLoading) {
      console.log('TP level change already in progress');
      return;
    }

    console.log('Changing TP level from', selectedTpLevel, 'to', newTpLevel);
    
    setIsLoading(true);
    setSelectedTpLevel(newTpLevel);
    setSelectedLevel(newTpLevel); // Keep backward compatibility
    setRiskTermsChosen(true);

    try {
      await saveUserTpLevel(newTpLevel);
      // Don't fetch stats here - let the Dashboard handle the refresh
    } catch (error) {
      console.error('Error changing TP level:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Change SL ratio independently
  const changeSlRatio = async (newSlRatio) => {
    if (!TP_SL_RATIOS.includes(newSlRatio)) {
      console.error('Invalid SL ratio:', newSlRatio);
      return;
    }

    // Prevent overlapping changes
    if (isLoading) {
      console.log('SL ratio change already in progress');
      return;
    }

    console.log('Changing SL ratio from', selectedTpSlRatio, 'to', newSlRatio);
    
    setIsLoading(true);
    setSelectedTpSlRatio(newSlRatio);
    setRiskTermsChosen(true);

    try {
      await saveUserTpSlRatio(newSlRatio);
      // Don't fetch stats here - let the Dashboard handle the refresh
    } catch (error) {
      console.error('Error changing SL ratio:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Change both TP and SL together (for combined selectors)
  const changeSelection = async (newTpLevel, newSlRatio) => {
    if (!TP_LEVELS.includes(newTpLevel) || !TP_SL_RATIOS.includes(newSlRatio)) {
      console.error('Invalid TP/SL selection:', newTpLevel, newSlRatio);
      return;
    }

    // Prevent multiple simultaneous calls
    if (isLoading) {
      console.log('Selection change already in progress, skipping...');
      return;
    }

    console.log('Changing selection from', selectedTpLevel, selectedTpSlRatio, 'to', newTpLevel, newSlRatio);

    setIsLoading(true);
    
    // Update all state together to prevent multiple renders
    setSelectedTpLevel(newTpLevel);
    setSelectedTpSlRatio(newSlRatio);
    setSelectedLevel(newTpLevel); // Keep backward compatibility
    setRiskTermsChosen(true);
    
    try {
      // Save preferences concurrently
      await Promise.all([
        saveUserTpLevel(newTpLevel),
        saveUserTpSlRatio(newSlRatio)
      ]);
      
      // Don't fetch stats here - let the Dashboard handle the refresh
      console.log('TP/SL selection updated successfully');
      
    } catch (error) {
      console.error('Error changing TP/SL selection:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch performance stats for a specific TP/SL combination
  const fetchLevelStats = async (tpLevel, slRatio = null, lookbackDays = 90, strategy = null, filters = null) => {
    // For backward compatibility, if slRatio is a number but looks like lookbackDays, adjust params
    if (typeof slRatio === 'number' && slRatio > 10 && slRatio <= 365) {
      // Old function call pattern: fetchLevelStats(level, lookbackDays, ...)
      lookbackDays = slRatio;
      slRatio = selectedTpSlRatio;
    }
    
    const actualSlRatio = slRatio || selectedTpSlRatio;
    const cacheKey = `${tpLevel}_${actualSlRatio}_${lookbackDays}_${strategy || 'all'}_${JSON.stringify(filters || {})}`;
    
    // Return cached data if available
    if (levelStats[cacheKey]) {
      return levelStats[cacheKey];
    }

    try {
      // Use enhanced chart-view-signal-summary endpoint with TP level and SL ratio
      const params = new URLSearchParams({
        tp_level: tpLevel.toString(),
        sl_ratio: actualSlRatio.toString(),
        days: lookbackDays.toString()
      });
      
      if (strategy) {
        params.append('strategy', strategy);
      }

      // Add filter parameters if provided
      if (filters) {
        if (filters.index?.length) {
          filters.index.forEach(idx => params.append('index', idx));
        }
        if (filters.strategy?.length) {
          filters.strategy.forEach(str => params.append('strategy', str));
        }
        if (filters.signalDirection && filters.signalDirection !== 'all') {
          params.append('signal', filters.signalDirection.toUpperCase());
        }
        if (filters.confidence?.[0]) {
          params.append('confidence', filters.confidence[0].toString());
        }
        // CRITICAL: When filtering by ongoing, we want ALL ongoing signals, not just latest per stock
        // So set latest_only to false when status filter is active
        if (filters.signalStatus === 'ongoing' || (filters.signalStatus && filters.signalStatus !== 'all')) {
          params.append('latest_only', 'false');
        }
        if (filters.pattern?.length) {
          filters.pattern.forEach(pat => params.append('pattern', pat));
        }
        if (filters.matchMode) {
          params.append('match_mode', filters.matchMode);
        }
        if (filters.instrumentType && filters.instrumentType !== 'all') {
          params.append('type', filters.instrumentType);
        }
        if (filters.status) {
          params.append('status', filters.status);
        }
      }

      const response = await fetch(`${API_BASE}/stocks/chart-view-signal-summary?${params}`);
      const data = await response.json();

      let stats = null;
      if (response.ok && data) {
        // Use multilevel summary data from enhanced endpoint
        const total = data.total || 0;
        const tp = data.hit_tp || 0;
        const sl = data.hit_sl || 0;
        const ongoing = data.ongoing || 0;
        
        stats = {
          level: tpLevel,
          winRate: total > 0 ? (tp / total * 100) : 0,
          totalSignals: total,
          tpCount: tp,
          slCount: sl,
          ongoingCount: ongoing,
          avgReturn: data.avg_return || 0,
          avgDaysToTP: data.avg_days_to_tp || 0,
          avgDaysToSL: data.avg_days_to_sl || 0,
          sharpeRatio: data.sharpe_ratio || 0,
          maxDrawdown: data.max_drawdown || 0,
          profitFactor: tp > 0 && sl > 0 ? tp / sl : 0,
          lastUpdated: new Date().toISOString()
        };
      }

      if (stats) {
        setLevelStats(prev => ({
          ...prev,
          [cacheKey]: stats
        }));

        return stats;
      }
    } catch (error) {
      console.error('Error fetching level stats:', error);
    }

    return null;
  };

  // Get stats for current level
  const getCurrentLevelStats = (lookbackDays = 90, strategy = null) => {
    const cacheKey = `${selectedLevel}_${lookbackDays}_${strategy || 'all'}`;
    return levelStats[cacheKey] || null;
  };

  // Get optimal TP/SL level recommendation
  const getOptimalLevel = async (criteria = 'sharpe_ratio') => {
    try {
      // Fetch stats for all levels and find the best one based on criteria
      const allLevels = await fetchMultipleLevelStats(TP_SL_LEVELS);
      
      let bestLevel = DEFAULT_TP_LEVEL;
      let bestValue = 0;
      let reason = 'Default recommendation';
      
      Object.entries(allLevels).forEach(([level, stats]) => {
        if (!stats) return;
        
        let value = 0;
        switch (criteria) {
          case 'sharpe_ratio':
            value = stats.sharpeRatio || 0;
            reason = 'Best risk-adjusted returns';
            break;
          case 'win_rate':
            value = stats.winRate || 0;
            reason = 'Highest win rate';
            break;
          case 'avg_return':
            value = stats.avgReturn || 0;
            reason = 'Highest average returns';
            break;
          case 'profit_factor':
            value = stats.profitFactor || 0;
            reason = 'Best profit factor';
            break;
          default:
            value = stats.sharpeRatio || 0;
        }
        
        if (value > bestValue) {
          bestValue = value;
          bestLevel = parseFloat(level);
        }
      });

      return {
        level: bestLevel,
        reason: reason,
        performance: allLevels[bestLevel]
      };
    } catch (error) {
      console.error('Error fetching optimal level:', error);
    }

    return null;
  };

  // Batch fetch stats for multiple levels (for comparison views)
  const fetchMultipleLevelStats = async (levels, lookbackDays = 90) => {
    const promises = levels.map(level => fetchLevelStats(level, lookbackDays));
    const results = await Promise.all(promises);
    
    return levels.reduce((acc, level, index) => {
      if (results[index]) {
        acc[level] = results[index];
      }
      return acc;
    }, {});
  };

  // Clear stats cache (useful when data needs refresh)
  const clearStatsCache = () => {
    setLevelStats({});
  };

  // Format level for display
  const formatLevel = (level, decimals = 1) => {
    return `${level.toFixed(decimals)}%`;
  };

  const value = {
    // State - New
    selectedTpLevel,
    selectedTpSlRatio,
    // KAN-64 item 5: false until the user has ever picked risk terms — the
    // journey's TP/SL step renders blank and Scan stays gated while false.
    riskTermsChosen,

    // State - Backward Compatibility
    selectedLevel,
    isLoading,
    isInitialized,
    levelStats,
    
    // Actions - New
    changeTpLevel,
    changeSlRatio,
    changeSelection,
    
    // Actions - Backward Compatibility
    changeLevel,
    fetchLevelStats,
    getCurrentLevelStats,
    getOptimalLevel,
    fetchMultipleLevelStats,
    clearStatsCache,
    
    // Utilities
    formatLevel,
    getRiskProfile: (level) => getRiskProfile(level || selectedTpLevel),
    getCombinedRiskProfile: (tp, sl) => getCombinedRiskProfile(tp || selectedTpLevel, sl || selectedTpSlRatio),
    
    // Constants - New
    TP_LEVELS,
    TP_SL_RATIOS,
    DEFAULT_TP_LEVEL,
    DEFAULT_TP_SL_RATIO,
    
    // Constants - Backward Compatibility
    TP_SL_LEVELS
  };

  return (
    <TPSLContext.Provider value={value}>
      {children}
    </TPSLContext.Provider>
  );
};

// Custom hook to use TPSL context
export const useTPSL = () => {
  const context = useContext(TPSLContext);
  if (!context) {
    throw new Error('useTPSL must be used within a TPSLProvider');
  }
  return context;
};

// HOC for components that need TPSL data
export const withTPSL = (Component) => {
  return (props) => {
    const tpslContext = useTPSL();
    return <Component {...props} tpsl={tpslContext} />;
  };
};

export default TPSLContext;